import { useRef, useCallback, useContext } from 'react';
import Modal from 'react-modal';
import classNames from 'classnames';
import { PopOutAudioContext } from '../../../components/content/pop-out-audio/pop-out-audio';
import type {
  PopOutAudioContextValue,
  PlaylistAudioItem,
} from '../../../components/content/pop-out-audio/types';
import { PlayStates } from '../../../components/content/pop-out-media-manager/types';
import icnClose from '../../../assets/ic_close.svg';
import styles from './modal-portal-comp.module.scss';
import { focusFirstElement } from '../../../global/helpers';

export type ModalProps = {
  children: React.ReactNode;
  onRequestClose?: (
    evt: React.MouseEvent<EventTarget> | React.KeyboardEvent<EventTarget>,
  ) => void;
  isOpen: boolean;
  shouldCloseOnEsc?: boolean;
  showCloseButton?: boolean;
  shouldCloseOnOuterClick?: boolean;
  isFullScreen?: boolean;
  transparent?: boolean;
  animated?: boolean;
  hideFocusOutline?: boolean;
  fitContentOnMobile?: boolean;
  ariaLabel?: string;
  removeMaxWidth?: boolean;
  topModal?: boolean;
  centerOnMobile?: boolean;
  initialFocusOnCloseButton?: boolean;
  shouldNotScroll?: boolean;
  shouldInitialFocus?: boolean;
};

type ModalClasses = {
  base?: string;
  afterOpen?: string;
  beforeClose?: string;
};

Modal.setAppElement(document.getElementById('react-view'));

const ModalPortalComp = ({
  onRequestClose,
  showCloseButton = true,
  shouldCloseOnOuterClick = true,
  children,
  isFullScreen = false,
  transparent = false,
  animated = false,
  hideFocusOutline = true,
  fitContentOnMobile = false,
  ariaLabel,
  removeMaxWidth = false,
  topModal = false,
  centerOnMobile = false,
  initialFocusOnCloseButton = false,
  shouldNotScroll = false,
  shouldInitialFocus = false,
  ...modalProps
}: ModalProps) => {
  const shouldShowCloseButton = showCloseButton && onRequestClose;

  const closeButtonRef = useCallback(closeButton => {
    if (initialFocusOnCloseButton && closeButton) {
      closeButton.focus();
    }
  }, []);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const audioParentRef = useRef<HTMLDivElement | null>(null);

  const {
    getCurrentlyActive,
    setAudioPlayerPortalOverrideParent,
    setAudioPlayerPortalOverrideTrigger,
    setModalDialogClosedLatch,
    browserPausesAudioOnCloseModal,
  } = useContext<PopOutAudioContextValue>(PopOutAudioContext);

  const modalContentClasses: ModalClasses = {
    base: classNames(
      styles.modal,
      isFullScreen && styles['modal--full-screen'],
      transparent && styles['modal--transparent'],
      animated && styles['content-animated'],
      hideFocusOutline && styles['modal--no-outline'],
      fitContentOnMobile && styles['modal--fit-content-mobile'],
      removeMaxWidth && styles['modal--no-max-width'],
      centerOnMobile && styles['modal--center-on-mobile'],
      shouldNotScroll && styles['modal--no-scroll'],
    ),
    afterOpen: animated ? styles['content-animated--after-open'] : '',
    beforeClose: animated ? styles['content-animated--before-close'] : '',
  };

  const onAfterOpen = () => {
    // tell pop-out audio player to move itself into the modal to enable keyboard nagivation while modal is open
    if (audioParentRef.current) {
      setAudioPlayerPortalOverrideParent(audioParentRef.current);
      setAudioPlayerPortalOverrideTrigger(true);
    }

    // focus first focusable element in modal for a11y standards
    if (shouldInitialFocus) {
      focusFirstElement(containerRef.current);
    }
  };

  // tell pop-out audio player to move back to its original location in main page
  const onAfterClose = () => {
    setAudioPlayerPortalOverrideParent(null);
    setAudioPlayerPortalOverrideTrigger(true);

    // Some browsers pause audio when a modal dialog closes.
    // The latch indicates that playback should be restarted after the pause
    const currentlyActive: PlaylistAudioItem | null = getCurrentlyActive();

    if (
      currentlyActive &&
      currentlyActive.state.playState === PlayStates.Playing &&
      browserPausesAudioOnCloseModal
    ) {
      setModalDialogClosedLatch(true);
    }
  };

  return (
    <Modal
      className={modalContentClasses}
      overlayClassName={{
        base: classNames(
          styles['overlay'],
          animated && styles['overlay--animated'],
          topModal && styles['overlay--top'],
        ),
        afterOpen: animated && styles['overlay--animated-after-open'],
        beforeClose: animated && styles['overlay--animated-before-close'],
      }}
      shouldCloseOnOverlayClick={shouldCloseOnOuterClick}
      onRequestClose={onRequestClose}
      onAfterOpen={onAfterOpen}
      onAfterClose={onAfterClose}
      closeTimeoutMS={animated ? 300 : null}
      contentLabel={ariaLabel}
      {...modalProps}
    >
      <div className={styles['modal-container-wrapper']} ref={containerRef}>
        {children}
      </div>
      <div className={styles['modal-audio-wrapper']} ref={audioParentRef} />
      {shouldShowCloseButton && (
        <div>
          <button
            ref={closeButtonRef}
            aria-label={polyglot.t('aria.close_modal')}
            onClick={onRequestClose}
            className={classNames(
              styles['close-button'],
              isFullScreen && styles['close-button--full-screen'],
            )}
          >
            <img src={icnClose} alt='' />
          </button>
        </div>
      )}
    </Modal>
  );
};

export default ModalPortalComp;
